'use client';

import * as React from 'react';
import { ToastList, type Toast } from './toast';
import { registerToast, unregisterToast } from '@/lib/toast';

interface ToasterContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToasterContext = React.createContext<ToasterContextValue | undefined>(
  undefined
);

export const useToaster = () => {
  const context = React.useContext(ToasterContext);
  if (!context) {
    throw new Error('useToaster must be used within a ToasterProvider');
  }
  return context;
};

interface ToasterProviderProps {
  children: React.ReactNode;
  maxVisible?: number;
  defaultDuration?: number;
}

export const ToasterProvider: React.FC<ToasterProviderProps> = ({
  children,
  maxVisible = 5,
  defaultDuration = 5000,
}) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timeoutsRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));

    // Clear timeout if it exists
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const addToast = React.useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const duration = toast.duration ?? defaultDuration;

      const newToast: Toast = {
        ...toast,
        id,
      };

      setToasts((prev) => [newToast, ...prev]);

      // Set auto-dismiss timeout
      if (duration > 0) {
        const timeout = setTimeout(() => {
          removeToast(id);
        }, duration);

        timeoutsRef.current.set(id, timeout);
      }

      return id;
    },
    [defaultDuration, removeToast]
  );

  const clearToasts = React.useCallback(() => {
    // Clear all timeouts
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();

    setToasts([]);
  }, []);

  // Register the toast function globally and cleanup on unmount
  React.useEffect(() => {
    registerToast(addToast);

    return () => {
      unregisterToast();
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, [addToast]);

  const value = React.useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      clearToasts,
    }),
    [toasts, addToast, removeToast, clearToasts]
  );

  return (
    <ToasterContext.Provider value={value}>
      {children}
      <ToastList
        toasts={toasts}
        onClose={removeToast}
        maxVisible={maxVisible}
      />
    </ToasterContext.Provider>
  );
};

export const Toaster: React.FC = () => {
  // This is a convenience component that just renders the toast container
  // The actual provider should be used in the layout
  return null;
};
